import { joinPaths } from "../navigation/utilities.js";
import { mergeParsers, type Parser } from "../parsing/index.js";
import type EndpointRouter from "./EndpointRouter.js";
import type { AnyEndpointRouter } from "./EndpointRouter.js";

export type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
export type QueryParamsBase = Record<string, string>;
export type PathParamsBase = Record<string, string>;
export type ContextBase = object;

export type AnyEndpoint = Endpoint<any, any, any, any, any>;

export type Inherit<Parent, Own> = unknown extends Parent
    ? Own
    : unknown extends Own
      ? Parent
      : Parent & Own;

export default class Endpoint<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Context extends ContextBase,
    Result,
> {
    private method: Method | null = null;
    private path: string | null = null;
    private pathParamsParser: Parser<PathParams> | null = null;
    private queryParamsParser: Parser<QueryParams> | null = null;
    private bodySchema: Parser<Body> | null = null;
    private contextParser: Parser<Context> | null = null;
    private resultParser: Parser<Result> | null = null;

    // Method

    setMethod(method: Method) {
        this.method = method;
        return this;
    }

    getMethod(): Method {
        if (!this.method) throw new Error("Method is not set");
        return this.method;
    }

    // Path

    setPath<NewPathParams extends PathParamsBase>(newPath: string, parser: Parser<NewPathParams>) {
        const endpoint = new Endpoint<NewPathParams, QueryParams, Body, Context, Result>();
        endpoint.method = this.method;
        endpoint.path = newPath;
        endpoint.pathParamsParser = parser;
        endpoint.queryParamsParser = this.queryParamsParser;
        endpoint.bodySchema = this.bodySchema;
        endpoint.contextParser = this.contextParser;
        endpoint.resultParser = this.resultParser;
        endpoint.parent = this.parent;
        return endpoint;
    }

    getPath(): string {
        return this.path || "";
    }

    getPathParamsParser(): Parser<PathParams> | null {
        return this.pathParamsParser;
    }

    // Query params

    setQueryParamsSchema<NewQueryParams extends QueryParamsBase>(
        queryParamsParser: Parser<NewQueryParams>,
    ) {
        const endpoint = new Endpoint<PathParams, NewQueryParams, Body, Context, Result>();
        endpoint.method = this.method;
        endpoint.path = this.path;
        endpoint.pathParamsParser = this.pathParamsParser;
        endpoint.queryParamsParser = queryParamsParser;
        endpoint.bodySchema = this.bodySchema;
        endpoint.contextParser = this.contextParser;
        endpoint.resultParser = this.resultParser;
        endpoint.parent = this.parent;
        return endpoint;
    }

    getQueryParamsParser(): Parser<QueryParams> | null {
        return this.queryParamsParser;
    }

    // Body
    setBodySchema<NewBody>(bodySchema: Parser<NewBody>) {
        const endpoint = new Endpoint<PathParams, QueryParams, NewBody, Context, Result>();
        endpoint.method = this.method;
        endpoint.path = this.path;
        endpoint.pathParamsParser = this.pathParamsParser;
        endpoint.queryParamsParser = this.queryParamsParser;
        endpoint.bodySchema = bodySchema;
        endpoint.contextParser = this.contextParser;
        endpoint.resultParser = this.resultParser;
        endpoint.parent = this.parent;
        return endpoint;
    }

    getBodyParser(): Parser<Body> | null {
        return this.bodySchema;
    }

    // Context

    // setContextParser<NewContext extends ContextBase>(contextParser: Parser<NewContext> | null) {
    //     const endpoint = new Endpoint<PathParams, QueryParams, Body, NewContext, Result>();
    //     endpoint.method = this.method;
    //     endpoint.path = this.path;
    //     endpoint.pathParamsParser = this.pathParamsParser;
    //     endpoint.queryParamsParser = this.queryParamsParser;
    //     endpoint.bodySchema = this.bodySchema;
    //     endpoint.contextParser = contextParser;
    //     endpoint.resultParser = this.resultParser;
    //     return endpoint;
    // }

    getContextParser(): Parser<Context> | null {
        return this.contextParser;
    }

    // Result
    setResultParser<NewResult>(resultParser: Parser<NewResult>) {
        const endpoint = new Endpoint<PathParams, QueryParams, Body, Context, NewResult>();
        endpoint.method = this.method;
        endpoint.path = this.path;
        endpoint.pathParamsParser = this.pathParamsParser;
        endpoint.queryParamsParser = this.queryParamsParser;
        endpoint.bodySchema = this.bodySchema;
        endpoint.contextParser = this.contextParser;
        endpoint.resultParser = resultParser;
        endpoint.parent = this.parent;
        return endpoint;
    }

    getResultParser(): Parser<Result> {
        if (!this.resultParser) throw new Error("Result parser is not set");
        return this.resultParser;
    }

    // Parent

    parent: AnyEndpointRouter | null = null;

    setRouter<RouterPathParams extends PathParamsBase, RouterContext extends ContextBase>(
        router: EndpointRouter<RouterPathParams, RouterContext>,
    ): Endpoint<RouterPathParams & PathParams, QueryParams, Body, RouterContext & Context, Result> {
        const endpoint = new Endpoint<
            RouterPathParams & PathParams,
            QueryParams,
            Body,
            RouterContext & Context,
            Result
        >();
        endpoint.method = this.method;
        endpoint.path = joinPaths(router.path, this.path);
        endpoint.pathParamsParser = mergeParsers(this.pathParamsParser, router.pathParamsParser);
        endpoint.queryParamsParser = this.queryParamsParser;
        endpoint.bodySchema = this.bodySchema;
        endpoint.contextParser = mergeParsers(this.contextParser, router.contextParser);
        endpoint.resultParser = this.resultParser;
        endpoint.parent = router;
        return endpoint;
    }

    getRouters(): Array<AnyEndpointRouter> {
        const output: Array<AnyEndpointRouter> = [];
        if (this.parent) output.push(...this.parent.getParents(), this.parent);
        return output;
    }
}
